import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FaArrowLeft,
  FaCreditCard,
  FaExclamationTriangle,
  FaFileInvoiceDollar,
  FaMapMarkerAlt,
  FaRegCalendarAlt,
  FaSearch
} from 'react-icons/fa';
import { Modal } from 'antd';
import StatusToast from '../../components/StatusToast/StatusToast';
import { getClubPaymentInfo } from '../../services/clubApplicationService';
import api from '../../services/api';
import './ClubPaymentPage.scss';

const daysOfWeek = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];

const ClubPaymentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [taxNumber, setTaxNumber] = useState(location.state?.taxNumber || '');
  const [loading, setLoading] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  const showToast = (type, message, duration = 4000) => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }

    setToast({ type, message });
    toastTimerRef.current = setTimeout(() => setToast(null), duration);
  };

  const fetchPaymentInfo = async (taxNo) => {
    const trimmedTaxNo = taxNo?.trim();

    if (!trimmedTaxNo) {
      setError('Lütfen bir vergi no veya TCKN girin.');
      return;
    }

    setLoading(true);
    setError('');
    setPaymentInfo(null);

    try {
      const response = await getClubPaymentInfo(trimmedTaxNo);
      if (response?.isSuccess !== false && response?.data) {
        setPaymentInfo(response.data);
      } else {
        setError(
          response?.resultMessage || 'Bu vergi numarasına ait onaylı kulüp ödeme bilgisi bulunamadı.'
        );
      }
    } catch (err) {
      console.error('Ödeme bilgisi sorgulanırken hata:', err);
      showToast('error', 'Ödeme bilgisi sorgulanırken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (location.state?.taxNumber) {
      fetchPaymentInfo(location.state.taxNumber);
    }
  }, [location.state?.taxNumber]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchPaymentInfo(taxNumber);
  };

  const handlePayment = async () => {
    if (!paymentInfo?.clubApplicationIds?.length) {
      return;
    }

    Modal.confirm({
      title: 'Ödeme Onayı',
      content: `${paymentInfo.clubName} için toplam ${paymentInfo.totalAmount} TL ödeme alınacaktır. Onaylıyor musunuz?`,
      okText: 'Evet',
      cancelText: 'Hayır',
      onOk: async () => {
        setLoading(true);
        try {
          const response = await api.post('/api/sports/club-applications/mark-paid', {
            clubApplicationIds: paymentInfo.clubApplicationIds,
            amount: paymentInfo.totalAmount
          });

          if (response?.isSuccess !== false) {
            showToast('success', 'Ödeme başarıyla alındı ve faturalandırıldı.');

            const refresh = await getClubPaymentInfo(taxNumber);
            if (refresh?.isSuccess !== false && refresh?.data) {
              setPaymentInfo(refresh.data);
              setError('');
            }
          } else {
            showToast('error', response?.resultMessage || 'Ödeme işlemi tamamlanamadı.');
          }
        } catch (err) {
          console.error('Ödeme işlemi sırasında hata:', err);
          showToast('error', 'Ödeme işlemi sırasında hata oluştu.');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  return (
    <div className="club-payment-page">
      <StatusToast toast={toast} onClose={() => setToast(null)} />

      <div
        className="page-header"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div className="header-icon">
            <FaFileInvoiceDollar />
          </div>
          <div>
            <h2>Kulüp Ödeme Ekranı</h2>
            <p>Kulüp vergi no veya TCKN ile tüm bölgelere ait ödeme dökümünü listeleyin.</p>
          </div>
        </div>
        <button
          className="btn btn-outline-secondary"
          onClick={() => navigate(-1)}
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <FaArrowLeft /> Geri Dön
        </button>
      </div>

      <div className="search-section card-box">
        <form onSubmit={handleSearch} className="search-form">
          <div className="form-group">
            <label>Vergi No / T.C. Kimlik No</label>
            <div className="input-with-icon">
              <input
                type="text"
                value={taxNumber}
                onChange={(e) => setTaxNumber(e.target.value)}
                placeholder="Örn: 1234567890"
              />
              <FaSearch className="icon" />
            </div>
          </div>
          <button type="submit" className="btn btn-primary search-btn" disabled={loading}>
            {loading ? 'Sorgulanıyor...' : 'Sorgula'}
          </button>
        </form>
        {error && (
          <div className="error-alert">
            <FaExclamationTriangle /> {error}
          </div>
        )}
      </div>

      {paymentInfo && (
        <div className="payment-details fade-in">
          <div className="club-summary card-box">
            <div className="summary-header">
              <h3>{paymentInfo.clubName}</h3>
              <span className={`status-badge ${paymentInfo.isExpired ? 'expired' : 'active'}`}>
                {paymentInfo.isExpired ? 'Üyelik Süresi Dolmuş' : 'Aktif Üye'}
              </span>
            </div>
            <div className="summary-info">
              <div className="info-item">
                <span className="label">Vergi No:</span>
                <span className="value">{paymentInfo.taxNumber}</span>
              </div>
              <div className="info-item">
                <span className="label">Üyelik Başlangıç:</span>
                <span className="value">
                  {paymentInfo.membershipStartDate
                    ? new Date(paymentInfo.membershipStartDate).toLocaleDateString('tr-TR')
                    : '-'}
                </span>
              </div>
              <div className="info-item">
                <span className="label">Üyelik Bitiş:</span>
                <span className="value">
                  {paymentInfo.membershipEndDate
                    ? new Date(paymentInfo.membershipEndDate).toLocaleDateString('tr-TR')
                    : '-'}
                </span>
              </div>
            </div>
          </div>

          {paymentInfo.regionBreakdowns && paymentInfo.regionBreakdowns.length > 0 ? (
            <div className="regions-container">
              {paymentInfo.regionBreakdowns.map((region, index) => (
                <div key={index} className="region-card card-box">
                  <div className="region-header">
                    <div className="region-title">
                      <FaMapMarkerAlt className="region-icon" />
                      <h4>{region.regionName}</h4>
                    </div>
                    <div className="region-facility">{region.facilityName}</div>
                  </div>

                  <div className="table-responsive">
                    <table className="assignments-table">
                      <thead>
                        <tr>
                          <th>Gün</th>
                          <th>Saat</th>
                          <th>Hafta Sayısı</th>
                          <th>Birim Fiyat</th>
                          <th className="text-right">Tutar</th>
                        </tr>
                      </thead>
                      <tbody>
                        {region.assignments?.map((slot, idx) => (
                          <tr key={idx}>
                            <td>{daysOfWeek[slot.dayOfWeek]}</td>
                            <td>
                              {slot.startTime} - {slot.endTime}
                            </td>
                            <td>{slot.weekCount} Hafta</td>
                            <td>{slot.pricePerHour} TL</td>
                            <td className="text-right font-weight-bold">{slot.subTotal} TL</td>
                          </tr>
                        ))}
                        {(!region.assignments || region.assignments.length === 0) && (
                          <tr>
                            <td colSpan="5" className="text-center text-muted">
                              Bu bölgede atanmış aktif saat bulunmuyor.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="region-footer">
                    <span>Bölge Toplamı:</span>
                    <span className="region-total">{region.regionTotal} TL</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state card-box">
              <FaRegCalendarAlt />
              <p>Bu kulübe atanmış herhangi bir saat bulunamadı.</p>
            </div>
          )}

          {paymentInfo.regionBreakdowns && paymentInfo.regionBreakdowns.length > 0 && (
            <div className="grand-total-card card-box">
              <div className="grand-total-info">
                <span>Genel Toplam Ödenecek Tutar</span>
                <h2>{paymentInfo.totalAmount} TL</h2>
              </div>
              <button
                className="btn btn-success checkout-btn"
                onClick={handlePayment}
                disabled={loading}
              >
                <FaCreditCard /> Ödemeyi Tamamla
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ClubPaymentPage;
